import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Users,
  Search,
  Plus,
  MoreVertical,
  Shield,
  Edit2,
  Trash2,
  Key,
  Mail,
} from 'lucide-react'
import { formatDate, getInitials } from '@/lib/utils'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'

type UserRole = 'admin' | 'technician' | 'customer_admin' | 'customer_staff'
type ApiUserRole = 'ADMIN' | 'TECHNICIAN' | 'CUSTOMER_ADMIN' | 'CUSTOMER_STAFF'

interface User {
  id: string
  name: string
  email: string
  role: UserRole
  status: 'active' | 'inactive' | 'pending'
  organizationId: string | null
  organizationName: string | null
  lastLogin: string | null
  createdAt: string
  avatarUrl: string | null
}

interface OrganizationOption {
  id: string
  name: string
}

function parseRoleInput(value: string | null | undefined, fallback: ApiUserRole = 'TECHNICIAN'): ApiUserRole {
  const roleInput = (value || '').trim().toLowerCase()
  switch (roleInput) {
    case 'admin':
      return 'ADMIN'
    case 'technician':
      return 'TECHNICIAN'
    case 'customer admin':
    case 'customer_admin':
    case 'org admin':
      return 'CUSTOMER_ADMIN'
    case 'customer staff':
    case 'customer_staff':
    case 'staff':
    case 'org staff':
      return 'CUSTOMER_STAFF'
    default:
      return fallback
  }
}

export default function UsersPage() {
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const currentUser = useAuthStore((state) => state.user)
  const queryClient = useQueryClient()
  const isAdmin = currentUser?.role === 'admin'
  const organizationFilter = searchParams.get('organizationId') || 'all'

  useEffect(() => {
    const role = searchParams.get('role')
    if (role === 'admin' || role === 'technician' || role === 'customer_admin' || role === 'customer_staff') {
      setRoleFilter(role)
    }
  }, [searchParams])

  const { data: users } = useQuery<User[]>({
    queryKey: ['users', roleFilter],
    queryFn: async () => {
      const response = await api.get('/users', {
        params: { role: roleFilter !== 'all' ? roleFilter : undefined },
      })
      return response.data
    },
    placeholderData: [
      {
        id: '1',
        name: 'John Smith',
        email: 'john@simcoaches.com',
        role: 'admin',
        status: 'active',
        organizationId: null,
        organizationName: null,
        lastLogin: new Date().toISOString(),
        createdAt: '2024-01-01T00:00:00Z',
        avatarUrl: null,
      },
      {
        id: '2',
        name: 'Sarah Johnson',
        email: 'sarah@simcoaches.com',
        role: 'technician',
        status: 'active',
        organizationId: null,
        organizationName: null,
        lastLogin: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        createdAt: '2024-01-15T00:00:00Z',
        avatarUrl: null,
      },
      {
        id: '3',
        name: 'Mike Wilson',
        email: 'mike@simcoaches.com',
        role: 'technician',
        status: 'active',
        organizationId: null,
        organizationName: null,
        lastLogin: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        createdAt: '2024-02-01T00:00:00Z',
        avatarUrl: null,
      },
      {
        id: '4',
        name: 'Tom Racing',
        email: 'tom@speedracing.com',
        role: 'customer_admin',
        status: 'active',
        organizationId: '1',
        organizationName: 'Speed Racing Center',
        lastLogin: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        createdAt: '2024-02-15T00:00:00Z',
        avatarUrl: null,
      },
      {
        id: '5',
        name: 'Emily Chen',
        email: 'emily@simlounge.com',
        role: 'customer_admin',
        status: 'pending',
        organizationId: '2',
        organizationName: 'Downtown Sim Lounge',
        lastLogin: null,
        createdAt: '2024-03-01T00:00:00Z',
        avatarUrl: null,
      },
    ],
  })

  const { data: organizations } = useQuery<OrganizationOption[]>({
    queryKey: ['customer-options-for-users'],
    queryFn: async () => {
      const response = await api.get('/customers')
      return response.data.map((organization: { id: string; name: string }) => ({
        id: organization.id,
        name: organization.name,
      }))
    },
    enabled: isAdmin,
    placeholderData: [],
  })

  const createUserMutation = useMutation({
    mutationFn: async (payload: {
      name: string
      email: string
      password: string
      role: ApiUserRole
      organizationId?: string
    }) => {
      const response = await api.post('/users', payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('User created')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create user')
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => {
      const response = await api.patch(`/users/${id}`, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('User updated')
      setSelectedUser(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user')
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('User deleted')
      setSelectedUser(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete user')
    },
  })

  const filteredUsers = users?.filter((user) => {
    if (organizationFilter !== 'all' && user.organizationId !== organizationFilter) {
      return false
    }

    if (search) {
      const searchLower = search.toLowerCase()
      return (
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return (
          <span className="badge bg-sc-accent-red/10 text-sc-accent-red">
            <Shield className="w-3 h-3" />
            Sim Coaches Admin
          </span>
        )
      case 'technician':
        return (
          <span className="badge bg-sc-primary/10 text-sc-primary">
            Sim Coaches Tech
          </span>
        )
      case 'customer_admin':
        return (
          <span className="badge bg-sc-accent-cyan/10 text-sc-accent-cyan">
            Customer Admin
          </span>
        )
      case 'customer_staff':
        return (
          <span className="badge bg-sc-text-muted/10 text-sc-text-secondary">
            Customer Staff
          </span>
        )
      default:
        return null
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-online">Active</span>
      case 'inactive':
        return <span className="badge badge-offline">Inactive</span>
      case 'pending':
        return (
          <span className="badge bg-sc-accent-yellow/10 text-sc-accent-yellow">
            Pending
          </span>
        )
      default:
        return null
    }
  }

  const handleInviteUser = () => {
    if (!isAdmin) {
      toast.error('Only admins can invite users')
      return
    }

    const name = window.prompt('User full name')?.trim()
    if (!name) return

    const email = window.prompt('User email')?.trim()
    if (!email) return

    const role = parseRoleInput(
      window.prompt('Role: admin, technician, customer admin, or customer staff', 'technician'),
      'TECHNICIAN'
    )

    let organizationId: string | undefined
    if ((role === 'CUSTOMER_ADMIN' || role === 'CUSTOMER_STAFF') && organizations && organizations.length > 0) {
      const options = organizations.map((organization) => `${organization.id}: ${organization.name}`).join('\n')
      const selection = window.prompt(`Organization id for this customer user:\n${options}`)?.trim()
      organizationId = selection || undefined
    }

    const password = window.prompt('Temporary password (min 8 chars)', 'changeme123')?.trim()
    if (!password || password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    createUserMutation.mutate({ name, email, password, role, organizationId })
  }

  const handleEditUser = (user: User) => {
    const name = window.prompt('User name', user.name)?.trim()
    if (!name) return

    const email = window.prompt('User email', user.email)?.trim()
    if (!email) return

    const role = parseRoleInput(
      window.prompt('Role: admin, technician, customer admin, or customer staff', user.role),
      user.role.toUpperCase() as ApiUserRole
    )

    const isActive = window.confirm('Should this user remain active? Click Cancel to mark inactive.')

    let organizationId: string | null | undefined = user.organizationId
    if ((role === 'CUSTOMER_ADMIN' || role === 'CUSTOMER_STAFF') && organizations && organizations.length > 0) {
      const options = organizations.map((organization) => `${organization.id}: ${organization.name}`).join('\n')
      organizationId = window.prompt(`Organization id for this customer user:\n${options}`, user.organizationId || '')?.trim() || null
    } else if (role !== 'CUSTOMER_ADMIN' && role !== 'CUSTOMER_STAFF') {
      organizationId = null
    }

    updateUserMutation.mutate({
      id: user.id,
      payload: { name, email, role, isActive, organizationId },
    })
  }

  const handleResetPassword = async (user: User) => {
    const password = window.prompt(`Set a new password for ${user.email}`, 'changeme123')?.trim()
    if (!password || password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    try {
      await api.post(`/users/${user.id}/reset-password`, { newPassword: password })
      toast.success(`Password reset for ${user.email}`)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset password')
    }
  }

  const handleResendInvite = async (user: User) => {
    const tempPassword = window.prompt(`Temporary password to share with ${user.email}`, 'changeme123')?.trim()
    if (!tempPassword || tempPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    try {
      await api.post(`/users/${user.id}/reset-password`, { newPassword: tempPassword })
      await navigator.clipboard.writeText([
        `PitStop login: https://pitstop.simcoaches.com/login`,
        `Email: ${user.email}`,
        `Temporary password: ${tempPassword}`,
      ].join('\n'))
      toast.success('Invite details copied to clipboard')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to prepare invite details')
    }
  }

  const handleDeleteUser = (user: User) => {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) {
      return
    }

    deleteUserMutation.mutate(user.id)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-sc-text-primary">Users</h1>
          <p className="text-sc-text-muted mt-1">
            Manage Sim Coaches staff and sim-center accounts
          </p>
        </div>
        <button className="btn-primary" onClick={handleInviteUser}>
          <Plus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      <div className="card">
        <div className="card-body py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sc-text-muted" />
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="all">All Roles</option>
              <option value="admin">Sim Coaches Admins</option>
              <option value="technician">Sim Coaches Technicians</option>
              <option value="customer_admin">Customer Admins</option>
              <option value="customer_staff">Customer Staff</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Organization</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers?.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-sc-primary/20 flex items-center justify-center text-sc-primary font-medium">
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(user.name)
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sc-text-primary">
                          {user.name}
                          {user.id === currentUser?.id && (
                            <span className="ml-2 text-xs text-sc-text-muted">(you)</span>
                          )}
                        </p>
                        <p className="text-sm text-sc-text-muted">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td>{getRoleBadge(user.role)}</td>
                  <td className="text-sc-text-secondary">
                    {user.organizationName || '-'}
                  </td>
                  <td>{getStatusBadge(user.status)}</td>
                  <td className="text-sc-text-muted text-sm">
                    {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                  </td>
                  <td>
                    <div className="relative">
                      <button
                        onClick={() =>
                          setSelectedUser(
                            selectedUser === user.id ? null : user.id
                          )
                        }
                        className="p-2 text-sc-text-muted hover:text-sc-text-primary hover:bg-sc-bg-light rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {selectedUser === user.id && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-sc-bg-medium border border-sc-border-medium rounded-lg shadow-sc-lg z-10 py-1">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit User
                          </button>
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                          >
                            <Key className="w-4 h-4" />
                            Reset Password
                          </button>
                          <button
                            onClick={() => handleResendInvite(user)}
                            className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            Resend Invite
                          </button>
                          {user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="w-full px-3 py-2 text-left text-sm text-sc-accent-red hover:bg-sc-bg-light flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete User
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers?.length === 0 && (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-sc-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-sc-text-primary mb-2">
              No users found
            </h3>
            <p className="text-sc-text-muted mb-4">
              {search
                ? 'Try adjusting your search terms'
                : 'Get started by inviting your first user'}
            </p>
            {!search && (
              <button className="btn-primary" onClick={handleInviteUser}>
                <Plus className="w-4 h-4" />
                Invite User
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
